import * as React from "react";
import styled from "styled-components";
import { v4 as uuidV4 } from "uuid";

import NotificationBanner, { NotificationBannerProps } from "./NotificationBanner";

interface NotificationBannerContextAttributes {
  notify: ShowBanner;
  hideBanner: HideBanner;
  hideBanners: HideBanners;
  banners: NotificationBannerObject[];
}

interface NotificationBannerAttributes {
  defaultDuration?: number;
}

type NotificationBannerConfig = Omit<NotificationBannerProps, "show" | "onClose" | "id"> & { id?: string; };
type ShowBanner = (config: NotificationBannerConfig) => string;
type HideBanner = (id: string) => void;
type HideBanners = () => void;

interface NotificationBannerObject extends NotificationBannerProps {
  id: string;
}

const initialContext: NotificationBannerContextAttributes = {
  notify: () => "",
  hideBanner: () => null,
  hideBanners: () => null,
  banners: []
};

/**
 * Notification banner context
 */
const NotificationBannerContext = React.createContext<NotificationBannerContextAttributes>(initialContext);

/**
 * Notification banner provider
 */
const NotificationBannerProvider: React.ReactFCWithChildren<NotificationBannerAttributes> = props => {
  const defaultDuration = props.defaultDuration as number;
  const [ banners, setBanners ] = React.useState<NotificationBannerObject[]>([]);

  /**
   * Hides all banners
   */
  const hideBanners: HideBanners = React.useCallback(() => {
    // Reset the banners state
    setBanners([]);
  }, []);

  /**
   * Hides a banner
   */
  const hideBanner: HideBanner = React.useCallback(id => {
    // Animate out the banner
    setBanners(currentBanners => currentBanners.map(banner => {
      // Update the matching banner's state to show = false
      if (id === banner.id) {
        banner.show = false;
      }

      return banner;
    }));

    // Remove the banner from the state after 400 milliseconds
    setTimeout(() => {
      setBanners(currentBanners => currentBanners.filter(banner => banner.id !== id));
    }, 400);
  }, []);

  /**
   * Show banner
   */
  const showBanner: ShowBanner = React.useCallback(config => {
    const duration = config.duration !== undefined ? config.duration : defaultDuration;

    const newBanner = {
      ...config,
      id: config.id || uuidV4(),
      show: true,
      duration
    };

    // Concat all the banners with the new one
    setBanners(existingBanners => [ ...existingBanners, newBanner ]);

    // Hide after the given duration if the duration is greater than 0
    if (newBanner.duration > 0) {
      setTimeout(() => {
        hideBanner(newBanner.id);
      }, newBanner.duration);
    }

    return newBanner.id;
  }, [ defaultDuration, hideBanner ]);

  return (
    <NotificationBannerContext.Provider
      value={{
        notify: showBanner,
        hideBanner,
        hideBanners,
        banners
      }}
    >
      {banners.length > 0 && (
        <StyledBanner className="notify">
          <>
            {banners.map(banner => (
              <NotificationBanner
                {...banner}
                key={banner.id}
                onClose={() => hideBanner(banner.id)}
              />
            ))}
          </>
        </StyledBanner>
      )}

      {props.children}
    </NotificationBannerContext.Provider>
  );
};

// Default props
NotificationBannerProvider.defaultProps = { defaultDuration: 5000 };

export default NotificationBannerProvider;
export const useNotificationBannerContext = () => React.useContext<NotificationBannerContextAttributes>(NotificationBannerContext);

const StyledBanner = styled.ul`
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 100;
  top: 0;
  left: 0;
  width: 100%;
`;
